import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableBody,
  CircularProgress,
  Stack,
  Button,
  TableContainer,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  RateReview as RateReviewIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorOutlineIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import {
  getStatusColor,
  formatFileName,
  formatFileType,
  isAttachmentVerified,
  getAttachmentDocumentFor,
  isAttachmentRejected,
  getAttachmentRejectReason,
  getAttachmentStatusMeta,
  isLeadDisabledLike,
} from './utils';
import { shouldShowAssignStatusForManager } from './leadAssignUserHelpers';
import { useAuth } from '../../hooks/useAuth';
import Can from '../permissions/Can';
import { useNavigate } from 'react-router-dom';
import { leadUploadDocumentsPath } from '../../navigations/path';

const LeadRow = ({
  row,
  onOpenMenu,
  processingId,
  onDownloadFile,
  onViewLead,
  onOpenInitialDeal,
  onCancelAttachment,
  onHoldIuw,
  onResolvedIuw,
  onAddAttachment,
  onRefreshLead,
  canEditLeads = false,
  canViewInitialDeal = false,
  showQcLog = false,
  leadGenRole = false,
  expanded,
  onToggleExpand,
}) => {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = expanded !== undefined ? expanded : internalOpen;
  const toggleOpen = () => {
    if (onToggleExpand) onToggleExpand();
    else setInternalOpen((o) => !o);
  };
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  const { user } = useAuth();
  const userRoleName = (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase();
  const rowStatusName = (row?.last_status_log?.status?.name || '').toLowerCase().trim();
  const CLOSER_ALLOWED_STATUSES = ['qualified', 'follow up (c)', 'follow c', 'qc approved', 'iuw declined'];
  const isCloserAllowedStatus = CLOSER_ALLOWED_STATUSES.includes(rowStatusName);
  const showInitialDeal = canViewInitialDeal && isCloserAllowedStatus;

  // Lead Gen / QC / QC Manager see QC log status (when present) instead of the lead status.
  // QC Declined: shown for all showQcLog roles (Lead Gen also gets red/locked UI).
  // QC Approved: shown for QC + QC Manager (same source as Declined); Lead Gen still
  // falls back to lead last_status_log when approved.
  const qcLogStatusName = (row.lead_q_c_log?.status?.name || '').toLowerCase().trim();
  const isQcViewerRole = userRoleName === 'qc' || userRoleName === 'qc manager';
  const isDeclinedForQc = showQcLog && Boolean(row.lead_q_c_log) && qcLogStatusName === 'qc declined';
  const isLockedForQc = leadGenRole && isDeclinedForQc;
  const maskValue = (v, visibleChars = 3) => {
    if (!v) return '';
    const str = String(v);
    const visible = str.slice(0, visibleChars);
    return visible + '•'.repeat(Math.min(str.length - visible.length, 10));
  };
  const shouldShowQcLogStatus =
    showQcLog &&
    Boolean(row.lead_q_c_log) &&
    (qcLogStatusName !== 'qc approved' || isQcViewerRole);
  const showAssignStatusForManager = shouldShowAssignStatusForManager(row, user);
  const displayStatusName = shouldShowQcLogStatus
    ? row.lead_q_c_log?.status?.name
    : showAssignStatusForManager
      ? 'Assign'
      : row.last_status_log?.status?.name;

  const handleDownload = async (file, index) => {
    setDownloadingIndex(index);
    await onDownloadFile(file, index);
    setDownloadingIndex(null);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          ...(isDeclinedForQc && {
            bgcolor: '#fff4f4',
            '&:hover': { bgcolor: '#fff4f4 !important' },
          }),
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={toggleOpen}
            disabled={isLockedForQc}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography
            variant="subtitle2"
            fontWeight={500}
            onClick={isLockedForQc ? undefined : () => onViewLead?.(row.id)}
            sx={{
              cursor: !isLockedForQc && onViewLead ? 'pointer' : 'default',
              color: !isLockedForQc && onViewLead ? 'primary.main' : 'inherit',
              '&:hover': !isLockedForQc && onViewLead ? { textDecoration: 'underline' } : undefined,
            }}
          >
            {row.name}
          </Typography>
        </TableCell>
        <TableCell>{row.dba_name || '—'}</TableCell>
        <TableCell>{isLockedForQc ? maskValue(row.email) : row.email}</TableCell>
        <TableCell>{isLockedForQc ? maskValue(row.phone) : row.phone}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Chip
              label={displayStatusName}
              color={getStatusColor(displayStatusName)}
              size="small"
            />
            {row.status?.status?.name === 'Meeting' && row.meeting_info && (
              <Chip
                label={`Meeting @ ${new Date(row.meeting_info.start_datetime).toLocaleString()}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {isLockedForQc ? (
              <Typography variant="body2" color="text.secondary">—</Typography>
            ) : row?.assignees?.length > 0 ? (
              row.assignees.map((assignee) => {
                const roleName = assignee?.roles?.[0]?.name;
                const label = `${assignee.name || assignee.username || 'Unnamed'}${roleName ? ` (${roleName})` : ''}`;
                return (
                  <Chip
                    key={assignee.id}
                    label={label}
                    variant="outlined"
                    size="small"
                  />
                );
              })
            ) : (
              <Chip label="Unassigned" variant="outlined" size="small" />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          {new Date(row.created_at).toLocaleDateString()}{' '}
          , {new Date(row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </TableCell>
        {canViewInitialDeal && (
          <TableCell sx={{ textAlign: 'center' }}>
            {!isLeadDisabledLike(row, user) && showInitialDeal && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenInitialDeal?.(row)}
                sx={{ whiteSpace: 'nowrap', minWidth: 96 }}
              >
                Initiate Deal
              </Button>
            )}
          </TableCell>
        )}
        <TableCell sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={(e) => onOpenMenu(e, row)}
            disabled={processingId === row.id || isLockedForQc}
          >
            {processingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={canViewInitialDeal ? 11 : 10}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              {/* Lead Description */}
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                {row.description || 'No description available'}
              </Typography>

              {/* Status Information */}
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Status Information
              </Typography>
              <Table size="small" aria-label="status-info" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Chip
                        label={row.last_status_log?.status?.name}
                        color={getStatusColor(row.status?.status?.name)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {row.status?.description || 'No description available'}
                    </TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {row.author?.name || 'System'}
                    </TableCell>
                    {/* <TableCell>
                      {row?.last_status_log?.assignee?.name || 'Unassigned'}
                    </TableCell> */}
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onViewLead?.(row.id)}
                >
                  View Details
                </Button>
                <Can permission="attachment-upload">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(leadUploadDocumentsPath(row.id))}
                  >
                    Upload Document
                  </Button>
                </Can>
              </Box>
              {/* Meeting Information */}
              {row.last_status_log?.status?.name === 'Meeting' &&
                row?.meetings && Array.isArray(row.meetings) && row.meetings.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                      Meeting Information
                    </Typography>

                    <Table size="small" aria-label="meeting-info" sx={{ mb: 3 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Time Zone</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Start Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>End Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.meetings.map((meeting, index) => (
                          <TableRow key={meeting.id || index}>
                            <TableCell>{meeting.time_zone || 'UTC'}</TableCell>
                            <TableCell>
                              {meeting.start_date_time
                                ? new Date(meeting.start_date_time).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {meeting.end_date_time
                                ? new Date(meeting.end_date_time).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {meeting.description || 'No meeting description'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row?.last_status_log?.status?.name || 'Meeting'}
                                color={getStatusColor(row?.last_status_log?.status?.name)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

              {/* Attachments */}
              <Can permission="attachment-list">
              {/* Attachments Checklist */}
              {(() => {
                const REQUIRED_DOCS = ['ein', 'ssn', 'driving_license', 'voided_cheque', 'additional'];
                const uploaded = Array.isArray(row?.attachments) ? row.attachments : [];
                const docMap = {};
                uploaded.forEach((a) => {
                  const key = (a?.title || '').toLowerCase().replace(/[\s_-]+/g, '_');
                  if (!docMap[key]) {
                    docMap[key] = a;
                  } else {
                    // prefer verified > non-rejected > rejected
                    const existing = docMap[key];
                    const aVerified = isAttachmentVerified(a);
                    const aRejected = isAttachmentRejected(a);
                    const existingRejected = isAttachmentRejected(existing);
                    const existingVerified = isAttachmentVerified(existing);
                    if (aVerified && !existingVerified) docMap[key] = a;
                    else if (!aRejected && existingRejected) docMap[key] = a;
                  }
                });
                return (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                      Attachments
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {REQUIRED_DOCS.map((doc) => {
                        const file = docMap[doc];
                        const label = doc.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                        const rejected = file ? isAttachmentRejected(file) : false;
                        const verified = file ? isAttachmentVerified(file) : false;
                        const uploaded_flag = Boolean(file);

                        let color, variant, icon, tooltip;
                        if (rejected) {
                          color = 'error'; variant = 'filled';
                          icon = <ErrorOutlineIcon fontSize="small" />;
                          tooltip = getAttachmentRejectReason(file) || 'Rejected';
                        } else if (verified) {
                          color = 'success'; variant = 'filled';
                          icon = <TaskAltIcon fontSize="small" />;
                          tooltip = 'Verified';
                        } else if (uploaded_flag) {
                          color = 'success'; variant = 'filled';
                          icon = <CheckCircleIcon fontSize="small" />;
                          tooltip = 'Uploaded';
                        } else {
                          color = 'warning'; variant = 'outlined';
                          icon = undefined;
                          tooltip = 'Pending';
                        }

                        return (
                          <Tooltip key={doc} title={tooltip} arrow>
                            <Chip
                              icon={icon}
                              label={label}
                              size="small"
                              color={color}
                              variant={variant}
                              sx={{ fontWeight: 500 }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}

              {/* Attachments Table */}
              {(row?.attachments && row?.attachments?.length > 0) && (
                <>
                 
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="attachments" sx={{ minWidth: 760 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded At</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.attachments.map((file, idx) => {
                          const fileName = formatFileName(file, idx);
                          const filePath = file?.path;
                          const fileType = formatFileType(file, filePath);
                          const isDownloading = downloadingIndex === idx;
                          const verified = isAttachmentVerified(file);
                          const rejected = isAttachmentRejected(file);
                          const rejectReason = getAttachmentRejectReason(file);

                          const rowBg = rejected
                            ? { bgcolor: '#fff4f4', borderLeft: '3px solid', borderColor: 'error.main' }
                            : verified
                            ? { bgcolor: '#f4fff6', borderLeft: '3px solid', borderColor: 'success.main' }
                            : {};

                          return (
                            <TableRow key={file?.id || idx} hover sx={rowBg}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Tooltip title={rejected ? 'Rejected' : verified ? 'Verified' : 'Pending review'}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {rejected ? (
                                        <CancelIcon fontSize="small" color="error" />
                                      ) : verified ? (
                                        <TaskAltIcon fontSize="small" color="success" />
                                      ) : (
                                        <CheckCircleOutlineIcon fontSize="small" color="action" />
                                      )}
                                    </Box>
                                  </Tooltip>
                                  <DescriptionIcon fontSize="small" color="action" />
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    title={fileName}
                                    sx={rejected ? { textDecoration: 'line-through', color: 'text.disabled' } : undefined}
                                  >
                                    {fileName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={fileType} size="small" />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getAttachmentDocumentFor(file)}
                                  {rejected && (
                                    <Chip
                                      icon={<ErrorOutlineIcon />}
                                      label={rejectReason ? `Rejected: ${rejectReason}` : 'Rejected'}
                                      color="error"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {verified && !rejected && (
                                    <Chip
                                      icon={<TaskAltIcon />}
                                      label="Verified"
                                      color="success"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const { label, color } = getAttachmentStatusMeta(file);
                                  return <Chip label={label} color={color} size="small" />;
                                })()}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {file?.created_at ? new Date(file.created_at).toLocaleDateString() : 'Unknown'}
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 0.5,
                                    minWidth: 160,
                                  }}
                                >
                                  {filePath && (
                                    <Can permission="attachment-view">
                                      <Tooltip title="View">
                                        <IconButton
                                          size="small"
                                          component="a"
                                          href={filePath}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Can>
                                  )}
                                  {filePath && (
                                    <Can permission="attachment-download">
                                      {isDownloading ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <Tooltip title="Download">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDownload(file, idx)}
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Can>
                                  )}
                                  {!rejected && (
                                    <Can permission="attachment-status">
                                      <Tooltip title="Update attachment status">
                                        <IconButton
                                          size="small"
                                          onClick={() => onCancelAttachment?.(row, file)}
                                          color="primary"
                                        >
                                          <RateReviewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Can>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onRefreshLead?.(row)}
                    >
                      Refresh Lead
                    </Button>
                    <Can permission="attachment-create">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onAddAttachment?.(row)}
                      >
                        Add Attachment
                      </Button>
                    </Can>
                    <Can permission="attachment-hold-iuw">
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => onHoldIuw?.(row)}
                      >
                        Hold IUW
                      </Button>
                    </Can>
                    <Can permission="attachment-resolved-iuw">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => onResolvedIuw?.(row)}
                      >
                        Resolved IUW
                      </Button>
                    </Can>
                  </Box>
                </>
              )}

              {(!row.attachments || row.attachments.length === 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No attachments available
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => onRefreshLead?.(row)}>
                      Refresh Lead
                    </Button>
                    <Can permission="attachment-create">
                      <Button variant="outlined" size="small" onClick={() => onAddAttachment?.(row)}>
                        Add Attachment
                      </Button>
                    </Can>
                  </Box>
                </Box>
              )}
              </Can>

              {/* Issue Information */}
              {(row.issue) && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                    Lead Issue Information
                  </Typography>
                  <Table size="small" aria-label="meeting-info" sx={{ mb: 3 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End Date & Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{row?.issue?.title || 'N/A'}</TableCell>
                        <TableCell>
                          {row?.issue?.description}
                        </TableCell>
                        <TableCell>
                          {new Date(row.issue?.created_at).toLocaleString()}
                        </TableCell>
                        {/*
                        <TableCell>
                          {row.last_status_log?.meeting?.description || 'No meeting description'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row?.last_status_log?.status?.name}
                            color={getStatusColor(row?.status?.meeting?.status?.name)}
                            size="small"
                          />
                        </TableCell> */}
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default LeadRow;